import axios from 'axios';
import store from '@/stores/store';

let instance = axios.create();
if (process.env.NODE_ENV === 'development') {
  instance = axios.create({
    withCredentials: true,
    baseURL: process.env.VUE_APP_API_HOST,
  });
}

instance.interceptors.response.use(
  (response: any) => {
    return response;
  },
  (err: any) => {
    if (err.response) {
      if (err.response.status === 400) {
        // BadRequest
      }
      if (err.response.status === 401) {
        store.dispatch('auth/unauthorizedRequest');
      }
      if (err.response.status === 403) {
        store.dispatch('auth/unauthorizedRequest');
      }
    }

    return Promise.reject(err.response);
  }
);

export default {
  account: {
    async postForgotPassword(forgotPassword: IForgotPassword) {
      const url = `/api/account/password/forgot`;
      const result = await instance.post(url, forgotPassword);
      return result.data as any;
    },
    async postValidatePasswordResetCode(validateCode: IResetPassword) {
      const url = `/api/account/password/reset/validate`;
      const result = await instance.post(url, validateCode);
      return result.data as IResetPassword;
    },
    async postResetPassword(resetPassword: IResetPassword) {
      const url = `/api/account/password/reset`;
      const result = await instance.post(url, resetPassword);
      return result.data as any;
    },
  },
  auth: {
    async postLogin(login: ILoginInformation) {
      const url = `/api/account/login`;
      const result = await instance.post(url, login);
      return result.data as IUserAuthProfile;
    },
    async postLogout() {
      const url = `/api/account/logout`;
      const result = await instance.post(url);
      return result.data as any;
    },
    async getProfile() {
      const url = `/api/account/profile`;
      const result = await instance.get(url);
      return result.data as IUserAuthProfile;
    },
  },
  cars: {
    async getCar(id: number) {
      const url = `/api/car/${id}`;
      const result = await instance.get(url);
      return result.data as INewCar;
    },

    async getCars() {
      const url = '/api/car';
      const result = await instance.get(url);
      return result.data as INewCar[];
    },

    async postNewCar(newCar: INewCar) {
      const url = '/api/car';
      const result = await instance.post(url, newCar);
      return result.data as INewCar;
    },

    async putCar(car: INewCar) {
      const url = `/api/car/${car.carNumber}`;
      const result = await instance.put(url, car);
      return result.data as INewCar;
    },

    async postCarImage(id: number, files: FormData) {
      const url = `/api/car/${id}/image`;
      const result = await instance.post(url, files, { headers: { 'Content-Type': 'multipart/form-data'} } );
      return result.data;
    },

    async deleteCarImage(id: number, imageId: number) {
      const url = `/api/car/${id}/image/${imageId}`;
      const result = await instance.delete(url);
      return result.data as IDocumentDetails[];
    },

    async getImageDetails(id: number) {
      const url = `/api/car/${id}/image`;
      const result = await instance.get(url);
      return result.data as IDocumentDetails[];
    },

    async getBrands() {
      const url = '/api/car/brands';
      const result = await instance.get(url);
      return result.data;
    },
  },
  contactInfo: {
    async postContactInfo(info: IContactInfo) {
      const url = `/api/user/${info.userId}/contactinfo`;
      const result = await instance.post(url, info);
      return result.data as IContactInfo;
    },
    async putContactInfo(info: IContactInfo) {
      const url = `/api/user/${info.id}/contactinfo`;
      const result = await instance.put(url, info);
      return result.data as IContactInfo;
    },

    async putSetMainContactInfo(info: IContactInfo) {
      const url = `/api/user/${info.userId}/mainContact`;
      const result = await instance.put(url, info);
      return result.data as IContactInfo[];
    },

    async getByUserId(id: string) {
      const url = `/api/user/${id}/contactinfo`;
      const result = await instance.get(url);
      return result.data as IContactInfo[];
    },

    async deleteContactInfo(userId: string, contactInfoId: number) {
      const url = `/api/user/${userId}/contactinfo/${contactInfoId}`;
      const result = await instance.delete(url);
      return result.data as IContactInfo;
    },
  },
  contracts: {
    async getContracts() {
      const url = '/api/carcontract';
      const result = await instance.get(url);
      return result.data as ICarContract[];
    },

    async getContractDetails(carNumber: number) {
      const url = `/api/carcontract/${carNumber}`;
      const result = await instance.get(url);
      return result.data as ICarContractDetails[];
    },

    async createNewContract(newCarContract: INewCarContract) {
      const url = `/api/carcontract`;
      const result = await instance.post(url, newCarContract).catch(error => {
        throw error;
      });
      return result.data as INewCarContract;
    },

    async putEditContract(carContract: INewCarContract) {
      const url = `/api/carcontract/${carContract.id}`;
      const result = await instance.put(url, carContract);
      return result.data as ICarContractDetails;
    },

    async postContractDocuments(id: number, files: FormData) {
      const url = `/api/carcontract/${id}/documents`;
      const result = await instance.post(url, files, { headers: { 'Content-Type': 'multipart/form-data'} });
      return result.data as IDocumentDetails[];
    },

    async getDocumentDetails(id: number) {
      const url = `/api/carcontract/${id}/documents`;
      const result = await instance.get(url);
      return result.data as IDocumentDetails[];
    },
    async deleteDocument(contractId: number, documentId: number) {
      const url = `/api/carcontract/${contractId}/documents/${documentId}`;
      const result = await instance.delete(url);
      return result.data as IDocumentDetails[];
    },
  },
  groups: {
    async getGroups() {
      const url = `/api/group`;
      const result = await instance.get(url);
      return result.data as IGroup[];
    },
    async getGroupUserProfiles(id: number) {
      const url = `/api/group/${id}/userProfiles`;
      const result = await instance.get(url);
      return result.data as IGroupUserProfile[];
    },
    async postJoinGroup(id: number, userProfile: IUserProfile) {
      const url = `/api/group/${id}/join`;
      const result = await instance.post(url, userProfile);
      return result.data as IGroupUserProfile[];
    },
    async postLeaveGroup(id: number, userProfile: IUserProfile) {
      const url = `/api/group/${id}/leave`;
      const result = await instance.post(url, userProfile);
      return result.data as IGroupUserProfile[];
    },
    async getAllGroupUserProfiles() {
      const url = `/api/group/userProfiles`;
      const result = await instance.get(url);
      return result.data as IGroupUserProfile[];
    },
  },
  members: {
    async getMembers() {
      const url = '/api/membership';
      const result = await instance.get(url);
      return result.data as IMember[];
    },

    async createMembership(membership: INewMember) {
      const url = `/api/membership/`;
      const result = await instance.post(url, membership);
      return result.data as IMember;
    },
    async cancelMembership(membership: IMember) {
      const url = `/api/membership/endMembership`;
      const result = await instance.put(url, membership);
      return result.data as IMember;
    }
  },
  memberNews: {
    async getPosts() {
      const url = `/api/memberNews`;
      const result = await instance.get(url);
      return result.data as IMemberNewsPost[];
    },
    async postNewPost(post: IMemberNewsPost) {
      const url = `/api/memberNews`;
      const result = await instance.post(url, post);
      return result.data as IMemberNewsPost;
    },
    async putEditPost(postId: number, post: IMemberNewsPost) {
      const url = `/api/memberNews/${postId}`;
      const result = await instance.put(url, post);
      return result.data as IMemberNewsPost;
    },
    async deletePost(postId: number) {
       const url = `/api/memberNews/${postId}`;
       const result = await instance.delete(url);
       return result.data as IMemberNewsPost;
    },
  },
  ownerships: {
    async getOwners() {
      const url = '/api/ownership';
      const result = await instance.get(url);
      return result.data as IOwnerListitem[];
    },

    async getOwnershipHistory(id: number) {
      const url = `/api/ownership/${id}`;
      const result = await instance.get(url);
      return result.data as IOwnershipHistoryItem;
    },

    async postChangeOwnership(newOwner: IOwnership) {
      const url = `/api/ownership/changeOwner`;
      const result = await instance.post(url, newOwner);
      return result.data as IShare;
    },
    async putOwnershipHistoryItem(ownership: IOwnershipHistoryItem) {
      const url = `/api/ownership/${ownership.id}`;
      const result = await instance.put(url, ownership);
      return result.data as IOwnershipHistoryItem;
    },

    async postOwnershipDocuments(id: number, files: FormData) {
      const url = `/api/ownership/${id}/documents`;
      const result = await instance.post(url, files, { headers: { 'Content-Type': 'multipart/form-data'} });
      return result.data as IDocumentDetails[];
    },

    async deleteOwnershipDocument(ownershipId: number, documentId: number) {
      const url = `/api/ownership/${ownershipId}/documents/${documentId}`;
      const result = await instance.delete(url);
      return result.data as IDocumentDetails[];
    },

    async getDocumentDetails(id: number) {
      const url = `/api/ownership/${id}/documents`;
      const result = await instance.get(url);
      return result.data as IDocumentDetails[];
    },
  },
  userProfile: {
    async getUserProfile(userId: string) {
      const url = `/api/profile/${userId}`;
      const result = await instance.get(url);
      return result.data as IUserProfile;
    },
    async getProfiles() {
      const url = `/api/profile`;
      const result = await instance.get(url);
      return result.data as IUserProfile[];
    },
    async getImageDetails(profileId: number) {
      const url = `/api/profile/${profileId}/image`;
      const result = await instance.get(url);
      return result.data as IDocumentDetails;
    },
    async postImage(profileId: number, file: FormData) {
      const url = `/api/profile/${profileId}/image`;
      const result = await instance.post(url, file, { headers: { 'Content-Type': 'multipart/form-data'} } );
      return result.data as IDocumentDetails;
    },
    async deleteImage(profileId: number, imageId: number) {
      const url = `/api/profile/${profileId}/image/${imageId}`;
      const result = await instance.delete(url);
      return result;
    },
    async postUserProfile(profile: IUserProfile) {
      const url = `/api/profile`;
      const result = await instance.post(url, profile);
      return result.data as IUserProfile;
    },
    async deleteUserProfile() {
      const url = `/api/profile`;
      const result = await instance.delete(url);
      return result.data as IUserProfile;
    },
    async getUserProfileGroups(profileId: number) {
      const url = `/api/profile/${profileId}/groupUserProfiles`;
      const result = await instance.get(url);
      return result.data as IGroupUserProfile[];
    },
  },
  users: {
    async getUsers() {
      const url = `/api/user`;
      const result = await instance.get(url);
      return result.data as IUser[];
    },
    async getById(id: string) {
      const url = `/api/user/${id}`;
      const result = await instance.get(url);
      return result.data as IUserDetails;
    },
    async postCreateNewUser(user: IUser) {
      const url = `/api/user`;
      const result = await instance.post(url, user);
      return result.data as IUser;
    },
    async getCarContracts(id: string) {
      const url = `/api/user/${id}/carcontracts`;
      const result = await instance.get(url);
      return result.data as ICarContract[];
    },
    async putUpdateUser(user: IUser) {
      const url = `/api/user/`;
      const result = await instance.put(url, user);
      return result.data as IUser;
    },
  },
  sharedDocuments: {
    async getSharedDocumentsDetails() {
      const url = `/api/sharedDocuments`;
      const result = await instance.get(url);
      return result.data as ISharedDocumentDetails[];
    },
    async addSharedDocument(document: any) {
      const url = `/api/sharedDocuments`;
      const result = await instance.post(url, document, { headers: { 'Content-Type': 'multipart/form-data' } });
      // const result = await instance.post(url, document);
      return result.data as ISharedDocumentDetails[];
    },
    async deleteSharedDocument(id: number) {
      const url = `/api/sharedDocuments/${id}`;
      const result = await instance.delete(url);
      return result.data as ISharedDocumentDetails[];
    },
  },
  shares: {
    async getShares() {
      const url = '/api/share';
      const result = await instance.get(url);
      return result.data as IShare[];
    },
    async getShareHistory(id: number) {
      const url = `/api/share/${id}/history`;
      const result = await instance.get(url);
      return result.data as IOwnershipHistoryItem[];
    },
    async getShareMembers(id: number) {
      const url = `/api/share/${id}/members`;
      const result = await instance.get(url);
      return result.data as IMember[];
    },
  },
};
